'use client'

import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import { Icon } from '@iconify/react'
import CustomAvatar from '../../../../../components/common/Template_Layouts/@core/components/mui/Avatar'
import { getCreditNoteDetailById } from '../../../../../utils/lead'
import OptionMenu from '../../../../../components/common/Template_Layouts/@core/components/option-menu'

const CreditNoteGrid = ({
    data = [],
    loading,
    permissions,
    onShowClick,
    onEditClick,
    onCancelClick,
    onDeleteClick,
    onRequestApprovalClick
}) => {
    const [selectedId, setSelectedId] = useState(data[0]?.Id || null);
    const [detailData, setDetailData] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    // ── Permission flags — same mapping as CreditNoteListTable ────────────────
    const canAdd    = permissions ? permissions.Add    !== false : true;
    const canEdit   = permissions ? permissions.Edit   !== false : true;
    const canView   = permissions ? permissions.View   !== false : true;
    const canDelete = permissions ? permissions.Delete !== false : true;

    useEffect(() => {
        if (!selectedId && data.length > 0) {
            setSelectedId(data[0].Id || data[0].TaskHistoryId);
        }
    }, [data, selectedId]);

    useEffect(() => {
        const fetchDetail = async () => {
            if (!selectedId) return;
            setIsDetailLoading(true);
            try {
                const response = await getCreditNoteDetailById({
                    Id: selectedId,
                    TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                });
                setDetailData(response);
            } catch (err) {
                console.error('Fetch Detail Error:', err);
            } finally {
                setIsDetailLoading(false);
            }
        };
        fetchDetail();
    }, [selectedId]);

    if (loading && data.length === 0) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;

    const renderActionMenu = (cn) => {
        const status = (cn.Status || cn.status || '')?.toLowerCase();

        // ── Mirror statusActionMenuHandler ────────────────────────────────────
        const showOption = canView ? {
            text: 'Show',
            icon: <Icon icon='tabler:eye' fontSize={20} />,
            menuItemProps: { onClick: () => onShowClick && onShowClick(cn) }
        } : null;

        const approvalOption = (cn.HasApprovalSetting && canAdd) ? {
            text: 'Request for Approval',
            icon: <Icon icon='tabler:send' fontSize={20} />,
            menuItemProps: { onClick: () => onRequestApprovalClick && onRequestApprovalClick(cn) }
        } : null;

        const editOption = canEdit ? {
            text: 'Edit',
            icon: <Icon icon='tabler:edit' fontSize={20} />,
            menuItemProps: { onClick: () => onEditClick && onEditClick(cn) }
        } : null;

        const refundOption = canEdit ? {
            text: 'Refund',
            icon: <Icon icon='tabler:refresh' fontSize={20} />,
            menuItemProps: { onClick: () => onEditClick && onEditClick(cn) }
        } : null;

        const cancelOption = canDelete ? {
            text: 'Cancel',
            icon: <Icon icon='tabler:x' fontSize={20} style={{ color: '#ef4444' }} />,
            menuItemProps: { onClick: () => onCancelClick && onCancelClick(cn), className: 'text-danger' }
        } : null;

        const deleteOption = canDelete ? {
            text: 'Delete',
            icon: <Icon icon='tabler:trash' fontSize={20} style={{ color: '#ef4444' }} />,
            menuItemProps: { onClick: () => onDeleteClick && onDeleteClick(cn), className: 'text-danger' }
        } : null;

        let normalised = status;
        if (normalised === 'send for approved') normalised = 'sent for approval';

        let options = [];
        switch (normalised) {
            case 'open':
            case 'edit':
                options = [approvalOption, showOption, editOption, cancelOption, deleteOption].filter(Boolean);
                break;
            case 'approved':
                options = [showOption, refundOption].filter(Boolean);
                break;
            case 'rejected':
            case 'declined':
                options = [showOption, editOption, cancelOption, deleteOption].filter(Boolean);
                break;
            case 'partially approved':
            case 'sent for approval':
            case 'cancelled':
            case 'refunded':
            case 'sent':
            default:
                options = [showOption].filter(Boolean);
                break;
        }

        return (
            <OptionMenu
                options={options}
                icon={<Icon icon='ri:more-2-fill' fontSize={22} />}
            />
        );
    };

    return (
        <Box sx={{ display: 'flex', width: '100%', flexGrow: 1, height: 'calc(100vh - 350px)', minHeight: '600px', gap: 4 }}>
            {/* Left Sidebar - List of Cards */}
            <Box sx={{ width: '350px', display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto', pr: 2, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, px: 2 }}>Credit Note List</Typography>
                {data.map((cn) => (
                    <Card
                        key={cn.Id || cn.TaskHistoryId}
                        onClick={() => setSelectedId(cn.Id || cn.TaskHistoryId)}
                        sx={{
                            cursor: 'pointer',
                            border: (selectedId === cn.Id || selectedId === cn.TaskHistoryId) ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                            backgroundColor: (selectedId === cn.Id || selectedId === cn.TaskHistoryId) ? '#eff6ff' : '#ffffff',
                            transition: 'all 0.2s ease',
                            '&:hover': { boxShadow: 2 }
                        }}
                    >
                        <CardContent sx={{ p: '12px !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                                <CustomAvatar skin='light' color='info' variant='rounded' size={32}>
                                    <Icon icon="ri:file-list-3-line" fontSize={18} />
                                </CustomAvatar>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {cn.CreditNoteNumber}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
                                <Chip
                                    label={cn.Status}
                                    size="small"
                                    variant="tonal"
                                    color={cn.Status?.toLowerCase() === 'open' ? 'primary' : cn.Status?.toLowerCase() === 'cancelled' ? 'error' : 'success'}
                                    sx={{ height: '20px', fontSize: '11px', fontWeight: 600 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{cn.IssueDate || cn.IssuedAt}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CustomAvatar skin='light' color='secondary' size={24} sx={{ fontSize: '10px' }}>
                                    {cn.ContactName?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                                </CustomAvatar>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>{cn.ContactName}</Typography>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Right Pane - Detail View */}
            <Box sx={{ flex: 1, backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #e2e8f0', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {isDetailLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography color="textSecondary">Loading details...</Typography>
                    </Box>
                ) : detailData ? (
                    <Box sx={{ p: 6 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <CustomAvatar skin='light' color='info' variant='rounded' size={48}>
                                    <Icon icon="ri:file-list-3-line" fontSize={28} />
                                </CustomAvatar>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{detailData.credit_note_number}</Typography>
                            </Box>
                            {renderActionMenu(detailData)}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 8 }}>
                            <CustomAvatar skin='light' color='primary' size={40}>
                                {detailData.contact_name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                            </CustomAvatar>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600 }}>Related To:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>{detailData.contact_name}</Typography>
                            </Box>
                        </Box>

                        <Grid container spacing={6} sx={{ mb: 8 }}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>Issue Date:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{detailData.issued_at}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>Created On:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{detailData.created_at}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>Status:</Typography>
                                    <Chip label={detailData.status} color="primary" variant="tonal" size="small" sx={{ width: 'fit-content' }} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>Amount:</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>₹ {detailData.amount}</Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ mb: 8 }} />

                        <Box sx={{ mb: 6 }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 2 }}>Description:</Typography>
                            <Box sx={{ p: 4, backgroundColor: '#f8fafc', borderRadius: 1, minHeight: '100px' }}>
                                <Typography variant="body2">{detailData.description || 'No description provided.'}</Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 4 }}>Line Items:</Typography>
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b' }}>ITEM</th>
                                            <th style={{ textAlign: 'center', padding: '12px', fontSize: '12px', color: '#64748b' }}>QTY</th>
                                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', color: '#64748b' }}>TOTAL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailData.creditNoteItems?.map((item, i) => (
                                            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '12px', fontSize: '13px' }}>
                                                    <Typography variant="subtitle2">{item.Name}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{item.Description}</Typography>
                                                </td>
                                                <td style={{ textAlign: 'center', padding: '12px', fontSize: '13px' }}>{item.Quantity}</td>
                                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px', fontWeight: 600 }}>₹ {item.Total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography color="textSecondary">Select a credit note to view details</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default CreditNoteGrid
